import { Provider } from '@angular/core';
import {
  ToolbarControlDescriptor,
  TOOLBAR_CONTROLS,
} from '../contracts/toolbar-control.tokens';
import {
  ClusterToggleControlComponent,
  CloseControlComponent,
  LabelsToggleControlComponent,
  PlayPauseControlComponent,
  SkipEndControlComponent,
  SkipStartControlComponent,
  SpeedControlComponent,
  StepBackControlComponent,
  StepForwardControlComponent,
  TimelineControlComponent,
} from './controls/toolbar-transport-controls';
import {
  DateRangeControlComponent,
  HistoryEntryControlComponent,
  LiveBadgeControlComponent,
  PlaybackHistoryControlComponent,
  TypeFilterControlComponent,
} from './controls/toolbar-live-controls';
import { SeparatorControlComponent } from './controls/toolbar-chrome-controls';

/**
 * Central registry of every available toolbar control. Adding a new control =
 * add a descriptor here + declare the component in the module. Which controls a
 * given sensor shows (and in what order) is decided separately by
 * `SENSOR_TOOLBAR_CONFIGS` — this file only says what CAN exist.
 */
const DESCRIPTORS: ToolbarControlDescriptor[] = [
  // Chrome. A separator has no default position — it only means anything
  // relative to the controls around it, so every config that uses one states
  // its `order` (and may use it more than once).
  { id: 'sep', component: SeparatorControlComponent, slots: ['live', 'playback', 'track', 'vessel'], defaultOrder: 0 },

  // Transport — reused verbatim by the playback (AOI snapshots) and track
  // (single-vessel path) slots. They read only `state$`, so they are entirely
  // operator- and dataset-agnostic.
  { id: 'close', component: CloseControlComponent, slots: ['playback'], defaultOrder: 0 },
  { id: 'play-pause', component: PlayPauseControlComponent, slots: ['playback', 'track'], defaultOrder: 10 },
  { id: 'skip-start', component: SkipStartControlComponent, slots: ['playback', 'track'], defaultOrder: 20 },
  { id: 'step-back', component: StepBackControlComponent, slots: ['playback', 'track'], defaultOrder: 30 },
  { id: 'timeline', component: TimelineControlComponent, slots: ['playback', 'track'], defaultOrder: 40 },
  { id: 'step-forward', component: StepForwardControlComponent, slots: ['playback', 'track'], defaultOrder: 50 },
  { id: 'skip-end', component: SkipEndControlComponent, slots: ['playback', 'track'], defaultOrder: 60 },
  { id: 'speed', component: SpeedControlComponent, slots: ['playback', 'track'], defaultOrder: 70 },
  // Also a LIVE control: name labels are a property of what is drawn, not of
  // playback, and the live snapshot is the view they are read off most.
  { id: 'labels', component: LabelsToggleControlComponent, slots: ['live', 'playback', 'track'], defaultOrder: 80 },
  // No `track` slot: this component toggles clustering on `context.controller`,
  // which in the track slot is the track — see `track-clusters` below, which is
  // the same button wired to the controller that actually owns the setting.
  { id: 'clusters', component: ClusterToggleControlComponent, slots: ['live', 'playback'], defaultOrder: 82, requires: ['clustering'] },

  // Shared (both slots)
  { id: 'type-filter', component: TypeFilterControlComponent, slots: ['live', 'playback'], defaultOrder: 90 },
  { id: 'date-range', component: DateRangeControlComponent, slots: ['playback'], defaultOrder: 100 },
  // The LOOKUP drawers — the controls that ask a question about the map rather
  // than change how it is drawn. Port calls answers where a hull has BEEN,
  // nearby answers who is AROUND it right now, search answers which hull to look
  // at in the first place. The trace entry below is the fourth of them and takes
  // the same two slots.
  //
  // They take the SAME TWO SLOTS, and that is the rule rather than a
  // coincidence: they belong on the bars that describe a STILL map (`live`, and
  // `vessel` for the empty one) and on neither replay bar. A transport is for
  // moving the clock; a question is something you ask about a scene you are
  // reading. On the narrow track bar the two were also competing for one row.
  //
  // Port calls and nearby have a second reason to be off `playback` that search
  // does not share: that bar has no selected vessel, so they would have no MMSI
  // to ask about — which is exactly why the original port-call button sat there
  // dead for a release before it was moved.
  //
  // `slots` is the whitelist the host filters against, so this list and
  // SENSOR_TOOLBAR_CONFIGS must agree or a config entry silently does nothing.

  // Live slot
  { id: 'live-badge', component: LiveBadgeControlComponent, slots: ['live'], defaultOrder: 0 },
  { id: 'history-entry', component: HistoryEntryControlComponent, slots: ['live'], defaultOrder: 120 },
  // Replays SAVED refreshes, so it presumes an operator whose live data arrives
  // as discrete paid pulls — the archive it replays is `pull-refresh`'s own.
  { id: 'playback-history', component: PlaybackHistoryControlComponent, slots: ['live'], defaultOrder: 125, requires: ['pull-refresh'] },

  // Voyage Radar's ten controls moved to
  // `ais/voyage-radar/toolbar/vr-toolbar-controls.providers.ts` (D7 amendment):
  // every one of them injects a VR controller, and `TOOLBAR_CONTROLS` is a
  // `multi` token, so a provider can contribute its own descriptors without
  // this file importing a single provider component. What is left here is what
  // any provider can mount — the transport, the toggles, the live chrome.
];

export const TOOLBAR_CONTROL_PROVIDERS: Provider[] = DESCRIPTORS.map((d) => ({
  provide: TOOLBAR_CONTROLS,
  useValue: d,
  multi: true,
}));
